import { LanguageVariantsSection } from "@/components/language-variants-section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateHoroscopeEntry,
  useHoroscopeEntry,
  useUpdateHoroscopeEntry,
  useUpdateHoroscopeEntryStatus,
} from "@/hooks/useHoroscopeEntries";
import { useHoroscopeSigns } from "@/hooks/useHoroscopeSigns";
import { useToast } from "@/hooks/use-toast";
import {
  HOROSCOPE_LANGUAGES as SUPPORTED_LANGUAGES,
  getLanguageLabel,
} from "@/lib/studio-constants";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronUp,
  Save,
  Send,
  Star,
  StickyNote,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Link, useLocation, useRoute } from "wouter";
import { z } from "zod";

const MAX_TITLE_LENGTH = 200;
const MAX_READING_LENGTH = 2000;
const MAX_NOTES_LENGTH = 1000;

const horoscopeFormSchema = z.object({
  zodiacSign: z.string().min(1, { message: "Zodiac sign is required" }),
  date: z
    .string()
    .min(1, { message: "Date is required" })
    .refine((val) => !isNaN(Date.parse(val)), {
      message: "Invalid date format",
    }),
  primaryLanguage: z
    .string()
    .min(2, { message: "Primary language is required" })
    .max(5)
    .refine((val) => SUPPORTED_LANGUAGES.some((l) => l.code === val), {
      message: `Language must be one of: ${SUPPORTED_LANGUAGES.map((l) => l.code).join(", ")}`,
    }),
  translations: z
    .array(
      z.object({
        languageCode: z
          .string()
          .min(2)
          .max(5)
          .refine((val) => SUPPORTED_LANGUAGES.some((l) => l.code === val)),
        title: z
          .string()
          .max(
            MAX_TITLE_LENGTH,
            `Title must not exceed ${MAX_TITLE_LENGTH} characters`,
          )
          .optional(),
        readingText: z
          .string()
          .min(1, { message: "Reading text is required" })
          .max(MAX_READING_LENGTH, {
            message: `Reading text must not exceed ${MAX_READING_LENGTH} characters`,
          }),
        isDefault: z.boolean().optional(),
      }),
    )
    .min(1, { message: "At least one translation is required" }),
  internalNotes: z
    .string()
    .max(
      MAX_NOTES_LENGTH,
      `Notes must not exceed ${MAX_NOTES_LENGTH} characters`,
    )
    .optional(),
});

type HoroscopeFormValues = z.infer<typeof horoscopeFormSchema>;

function getDefaultFormValues(
  primaryLanguage: string = "en",
): HoroscopeFormValues {
  return {
    zodiacSign: "",
    date: "",
    primaryLanguage,
    translations: [
      {
        languageCode: primaryLanguage,
        title: "",
        readingText: "",
        isDefault: true,
      },
    ],
    internalNotes: "",
  };
}

export default function CreateHoroscopePage() {
  const [, setLocation] = useLocation();
  const [editMatch, editParams] = useRoute("/studio/horoscope/edit/:id");
  const editId = editMatch ? (editParams?.id ?? null) : null;
  const isEditMode = !!editId;

  const [selectedTranslationIndex, setSelectedTranslationIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const { toast } = useToast();
  const [identityOpen, setIdentityOpen] = useState(true);
  const [refinementOpen, setRefinementOpen] = useState(false);

  const form = useForm<HoroscopeFormValues>({
    resolver: zodResolver(horoscopeFormSchema),
    defaultValues: getDefaultFormValues("en"),
  });

  const { data: signs = [] } = useHoroscopeSigns({ language: "en" });
  const {
    data: entry,
    isLoading: entryLoading,
    isError: entryError,
  } = useHoroscopeEntry(editId);
  const createMutation = useCreateHoroscopeEntry();
  const updateMutation = useUpdateHoroscopeEntry();
  const updateStatusMutation = useUpdateHoroscopeEntryStatus();

  const {
    fields: translationFields,
    append: appendTranslation,
    remove: removeTranslation,
  } = useFieldArray({
    control: form.control,
    name: "translations",
  });

  useEffect(() => {
    if (!entry || !isEditMode) return;
    form.reset({
      zodiacSign: entry.zodiacSign,
      date: entry.date,
      primaryLanguage: entry.primaryLanguage,
      translations: entry.translations.map((t) => ({
        languageCode: t.languageCode,
        title: t.title || "",
        readingText: t.readingText,
        isDefault: t.isDefault,
      })),
      internalNotes: entry.internalNotes || "",
    });
    setSelectedTranslationIndex(0);
  }, [entry, isEditMode, form]);

  const selectedTranslation =
    form.watch("translations")[selectedTranslationIndex];
  const translations = form.watch("translations");

  const entryStatus = entry?.status;
  const isEditable =
    !isEditMode ||
    entryStatus === "draft" ||
    entryStatus === "changes_requested";

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !translations.some((t) => t.languageCode === lang.code),
  );

  const addLanguage = (languageCode: string) => {
    if (translations.some((t) => t.languageCode === languageCode)) return;
    appendTranslation({
      languageCode,
      title: "",
      readingText: "",
      isDefault: false,
    });
    setSelectedTranslationIndex(translations.length);
  };

  const handleAddVariant = (languageCode: string) => {
    addLanguage(languageCode);
  };

  const handleSetDefault = (index: number) => {
    const translation = translations[index];
    if (translation) {
      handleSetPrimaryLanguage(translation.languageCode);
    }
  };

  const handleRequestRemove = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const handleConfirmRemove = () => {
    if (pendingRemoveIndex !== null) {
      handleRemoveLanguage(pendingRemoveIndex);
      setPendingRemoveIndex(null);
    }
  };

  const handleCancelRemove = () => {
    setPendingRemoveIndex(null);
  };

  const handleVariantChange = (index: number) => {
    setSelectedTranslationIndex(index);
  };

  const syncPrimaryLanguage = (primaryLanguage: string) => {
    form.setValue("primaryLanguage", primaryLanguage, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue(
      "translations",
      form.getValues("translations").map((translation) => ({
        ...translation,
        isDefault: translation.languageCode === primaryLanguage,
      })),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleSetPrimaryLanguage = (languageCode: string) => {
    syncPrimaryLanguage(languageCode);
  };

  const handleRemoveLanguage = (index: number) => {
    const currentTranslations = form.getValues("translations");
    const translationToRemove = currentTranslations[index];

    if (
      !translationToRemove ||
      translationToRemove.languageCode === form.getValues("primaryLanguage")
    ) {
      return;
    }

    removeTranslation(index);

    if (selectedTranslationIndex === index) {
      setSelectedTranslationIndex(Math.max(0, index - 1));
      return;
    }

    if (selectedTranslationIndex > index) {
      setSelectedTranslationIndex(selectedTranslationIndex - 1);
    }
  };

  const buildPayload = (formData: HoroscopeFormValues) => {
    const translationsWithDefault = formData.translations.map((t) => ({
      ...t,
      isDefault: t.languageCode === formData.primaryLanguage,
    }));
    return { translationsWithDefault };
  };

  const runSave = async (
    formData: HoroscopeFormValues,
    afterCreate?: (id: string) => void,
    afterUpdate?: () => void,
  ) => {
    const { translationsWithDefault } = buildPayload(formData);

    if (isEditMode && editId) {
      updateMutation.mutate(
        {
          id: editId,
          data: {
            translations: translationsWithDefault,
            internalNotes: formData.internalNotes || undefined,
            primaryLanguage: formData.primaryLanguage,
          },
        },
        { onSuccess: () => afterUpdate?.() },
      );
      return;
    }

    const dateObj = new Date(formData.date);
    if (isNaN(dateObj.getTime())) return;
    const isoDate = dateObj.toISOString().split("T")[0];

    createMutation.mutate(
      {
        zodiacSign: formData.zodiacSign,
        date: isoDate,
        primaryLanguage: formData.primaryLanguage,
        translations: translationsWithDefault,
        internalNotes: formData.internalNotes || undefined,
      },
      {
        onSuccess: (createdEntry) => afterCreate?.(createdEntry.id),
      },
    );
  };

  // Language fields render one tab at a time, so jump to the first tab with
  // an error or a failed Save would look like it did nothing.
  const validate = async () => {
    const valid = await form.trigger();
    if (!valid) {
      // getFieldState reads the live result; formState may still be the
      // pre-trigger render.
      const invalid = (name: Parameters<typeof form.getFieldState>[0]) =>
        form.getFieldState(name).invalid;
      const idx = form
        .getValues("translations")
        .findIndex((_, i) => invalid(`translations.${i}`));
      if (idx >= 0) setSelectedTranslationIndex(idx);
      // EVO-103: errors can sit in a collapsed card (sign, date, notes), so
      // open it and say so, or Submit looks like it did nothing.
      if (invalid("zodiacSign") || invalid("date")) setIdentityOpen(true);
      if (invalid("internalNotes")) setRefinementOpen(true);
      toast({
        title: "Please fix the highlighted fields",
        variant: "destructive",
      });
    }
    return valid;
  };

  // After the first save, open the saved entry so later saves update it
  // instead of re-creating it (server rejects the duplicate sign/date/lang).
  const openSavedEntry = (id: string) =>
    setLocation(`/studio/horoscope/edit/${id}`, { replace: true });

  const handleSave = async () => {
    if (!(await validate())) return;
    await runSave(form.getValues(), openSavedEntry);
  };

  const submitEntry = (id: string, onFail?: () => void) =>
    updateStatusMutation.mutate(
      { id, data: { status: "submitted" } },
      { onSuccess: () => setLocation("/studio/horoscope"), onError: onFail },
    );

  const handleSubmit = async () => {
    if (!(await validate())) return;
    const formData = form.getValues();

    if (isEditMode && editId) {
      await runSave(formData, undefined, () => submitEntry(editId));
      return;
    }

    // If the status change fails the draft already exists: open it so a retry
    // updates it instead of hitting "Entry already exists".
    await runSave(formData, (id) => submitEntry(id, () => openSavedEntry(id)));
  };

  const translationErrors =
    form.formState.errors.translations?.[selectedTranslationIndex];

  const onSubmit = (_data: HoroscopeFormValues) => {
    void handleSave();
  };

  if (isEditMode && entryLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isEditMode && (entryError || !entry)) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-muted-foreground">
            Entry not found or failed to load.
          </p>
          <Link href="/studio/horoscope">
            <Button variant="outline" className="mt-4">
              Back to Horoscope
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div
          className="p-6 lg:p-8 space-y-6 max-w-4xl mx-auto"
          data-testid="create-horoscope-page"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Link href="/studio/horoscope">
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="button-back"
                  type="button"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <h1
                className="font-serif text-2xl font-semibold tracking-tight"
                data-testid="text-page-title"
              >
                {isEditMode ? "Edit Horoscope" : "Create Horoscope"}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {isEditable && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSave}
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                    data-testid="button-save"
                    type="button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    className="evo-gradient text-white border-0"
                    onClick={handleSubmit}
                    disabled={
                      createMutation.isPending ||
                      updateMutation.isPending ||
                      updateStatusMutation.isPending
                    }
                    data-testid="button-submit"
                    type="button"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {entryStatus === "changes_requested"
                      ? "Resubmit for Approval"
                      : "Submit for Approval"}
                  </Button>
                </>
              )}
              {!isEditable && isEditMode && (
                <span
                  className="text-sm text-muted-foreground"
                  data-testid="text-readonly-notice"
                >
                  Read-only ({entryStatus}). Use review queue to act.
                </span>
              )}
            </div>
          </div>

          <Collapsible open={identityOpen} onOpenChange={setIdentityOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer px-4 py-4 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Star className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="space-y-1.5">
                        <CardTitle className="text-lg">
                          Horoscope Identity
                        </CardTitle>
                        <CardDescription>
                          Set the zodiac sign and date
                        </CardDescription>
                      </div>
                    </div>
                    {identityOpen ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-4 pb-4 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="zodiacSign"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Zodiac Sign</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isEditMode}
                          >
                            <FormControl>
                              <SelectTrigger
                                id="zodiac-sign"
                                data-testid="select-zodiac-sign"
                              >
                                <SelectValue placeholder="Select sign" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {signs.map((sign) => (
                                <SelectItem key={sign.slug} value={sign.slug}>
                                  {sign.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date</FormLabel>
                          <div className="relative">
                            <FormControl>
                              <Input
                                id="date"
                                type="date"
                                value={field.value}
                                onChange={field.onChange}
                                className="h-9 rounded-md pr-9 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                data-testid="input-date"
                                disabled={isEditMode}
                              />
                            </FormControl>
                            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <LanguageVariantsSection
            variants={translations.map((t) => ({
              languageCode: t.languageCode,
              isDefault: t.languageCode === form.watch("primaryLanguage"),
            }))}
            activeVariantIndex={selectedTranslationIndex}
            onActiveVariantChange={handleVariantChange}
            availableLanguages={availableLanguages}
            onAddVariant={handleAddVariant}
            onSetDefault={handleSetDefault}
            pendingRemoveIndex={pendingRemoveIndex}
            onRequestRemove={handleRequestRemove}
            onConfirmRemove={handleConfirmRemove}
            onCancelRemove={handleCancelRemove}
          >
            {selectedTranslation && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Title ({getLanguageLabel(selectedTranslation.languageCode)})
                    (optional)
                  </Label>
                  <Input
                    id="title"
                    value={selectedTranslation.title || ""}
                    onChange={(e) => {
                      const newTranslations = [...translations];
                      newTranslations[selectedTranslationIndex] = {
                        ...newTranslations[selectedTranslationIndex],
                        title: e.target.value,
                      };
                      form.setValue("translations", newTranslations);
                      if (translationErrors?.title) {
                        void form.trigger(
                          `translations.${selectedTranslationIndex}.title`,
                        );
                      }
                    }}
                    placeholder="e.g. Daily Aries Horoscope"
                    data-testid="input-title"
                  />
                  {translationErrors?.title && (
                    <p
                      className="text-sm font-medium text-destructive"
                      data-testid="error-title"
                    >
                      {translationErrors.title.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reading-text">
                    Reading Text (
                    {getLanguageLabel(selectedTranslation.languageCode)})
                  </Label>
                  <Textarea
                    id="reading-text"
                    rows={6}
                    value={selectedTranslation.readingText}
                    onChange={(e) => {
                      const newTranslations = [...translations];
                      newTranslations[selectedTranslationIndex] = {
                        ...newTranslations[selectedTranslationIndex],
                        readingText: e.target.value,
                      };
                      form.setValue("translations", newTranslations);
                      if (translationErrors?.readingText) {
                        void form.trigger(
                          `translations.${selectedTranslationIndex}.readingText`,
                        );
                      }
                    }}
                    placeholder="Enter the horoscope reading..."
                    data-testid="input-reading-text"
                  />
                  {translationErrors?.readingText && (
                    <p
                      className="text-sm font-medium text-destructive"
                      data-testid="error-reading-text"
                    >
                      {translationErrors.readingText.message}
                    </p>
                  )}
                </div>
              </>
            )}
          </LanguageVariantsSection>

          <Collapsible open={refinementOpen} onOpenChange={setRefinementOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer transition-colors px-4 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <StickyNote className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="space-y-1.5">
                        <CardTitle className="text-lg">Refinement</CardTitle>
                        <CardDescription>
                          Internal notes and metadata
                        </CardDescription>
                      </div>
                    </div>
                    {refinementOpen ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-4 pb-4 pt-4">
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium leading-none">
                      Internal Notes
                    </h3>
                    <Textarea
                      id="internal-notes"
                      rows={5}
                      maxLength={MAX_NOTES_LENGTH}
                      className="resize-y min-h-[120px]"
                      value={form.watch("internalNotes")}
                      onChange={(e) =>
                        form.setValue("internalNotes", e.target.value)
                      }
                      placeholder="Add internal notes visible only to admins..."
                      data-testid="input-internal-notes"
                    />
                    {form.formState.errors.internalNotes && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.internalNotes.message}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      These notes are only visible to administrators and will
                      not be shown to users.
                    </p>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </form>
    </Form>
  );
}
